import React, { useState } from 'react';
import { Application } from '../../shared/types/shift';
import { parseISO, format } from '../../shared/utils/dateUtils';
import { getDisplayStatus } from '../../shared/utils/get-status-color';
import { Avatar } from '../../components/avatar';
import {
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import Spinner from '../../components/loading/spinner';
import useApiService from '../../hooks/useApiService';

interface ExtendedApplication extends Application {
  hasOverlap?: boolean;
  overlappingShifts?: Array<{
    shift_id: string;
    start_time: string;
    end_time: string;
  }>;
}

type ApplicationRowProps = {
  application: ExtendedApplication;
  refetchData: () => void;
  onApplicationClick: (clinicianId: string) => void;
  openingId: string;
  showRemove?: boolean;
  hasSelectedApplication?: boolean;
  isSelectedApplication?: boolean;
  shiftStatus?: string;
  isShiftStartPassed?: boolean;
  disableApplicationActions: boolean;
};

const ApplicationRow: React.FC<ApplicationRowProps> = ({
  application,
  refetchData,
  onApplicationClick,
  openingId,
  showRemove = false,
  hasSelectedApplication = false,
  isSelectedApplication = false,
  shiftStatus,
  isShiftStartPassed = false,
  disableApplicationActions,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const api = useApiService();

  // -------------------------------------------------------------
  const handleAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setErrorMessage(null);
    setIsAccepting(true);
    try {
      await api.get(`/applications/accept-clinician`, {
        params: { application_id: application.application_id, opening_id: openingId },
      });
      refetchData();
    } catch (err: any) {
      const msg = err.response?.data?.message ?? 'Failed to accept application.';
      setErrorMessage(msg);
    } finally {
      setIsAccepting(false);
    }
  };

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setErrorMessage(null);
    setIsRemoving(true);
    try {
      await api.get(`/applications/remove-clinician`, {
        params: { application_id: application.application_id, opening_id: openingId },
      });
      refetchData();
    } catch (err: any) {
      const msg = err.response?.data?.message ?? 'Failed to remove clinician.';
      setErrorMessage(msg);
    } finally {
      setIsRemoving(false);
    }
  };

  const handleReject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setErrorMessage(null);
    setIsRejecting(true);
    try {
      await api.patch(`/applications/reject/${application.application_id}`, {});
      refetchData();
    } catch (err: any) {
      const msg = err.response?.data?.message ?? 'Failed to reject application.';
      setErrorMessage(msg);
    } finally {
      setIsRejecting(false);
    }
  };

  const handleRowClick = () => {
    if (application.clinicians?.clinician_id) {
      onApplicationClick(application.clinicians.clinician_id);
    }
  };

  const clinician = application.clinicians;
  const person = clinician?.people;

  const currentStatus =
    (shiftStatus === 'CONFIRMED' ||
      shiftStatus === 'CLOCKED_IN' ||
      shiftStatus === 'COMPLETED') &&
    isSelectedApplication
      ? shiftStatus
      : application.status;

  // Determine if Remove button should be disabled due to selected + shift started
  const isRemoveDisabledDueToStart =
    isSelectedApplication && isShiftStartPassed;

  // Final disable state for Remove button
  const isRemoveButtonDisabled =
    isRemoving ||
    disableApplicationActions ||
    isRemoveDisabledDueToStart;

  return (
    <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div
        className="w-full cursor-pointer rounded-xl border bg-white p-4 shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg md:flex-1"
        style={{ borderColor: '#E5E7EB' }}
        onClick={handleRowClick}
      >
        <div className="flex items-center space-x-4">
          <Avatar
            src={person?.photo}
            alt={`${person?.firstname ?? ''} ${person?.lastname ?? ''}`}
            className="size-12 rounded-full border-2 p-0.5"
            style={{ borderColor: '#FBBF24' }}
          />
          <div className="flex-1 space-y-1 min-w-0">
            <div className="flex items-center justify-between">
              <p className="font-bold text-gray-900">
                {person?.firstname} {person?.lastname}
              </p>
              {currentStatus !== 'PENDING' && (
                <span
                  className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                  style={{
                    backgroundColor:
                      getDisplayStatus(currentStatus) === 'Confirmed'
                        ? '#E0EDE2'
                        : getDisplayStatus(currentStatus) === 'Open'
                        ? '#E0E8ED'
                        : getDisplayStatus(currentStatus) === 'Cancelled'
                        ? '#FFEAEA'
                        : getDisplayStatus(currentStatus) === 'Selected'
                        ? '#FFF8E6'
                        : getDisplayStatus(currentStatus) === 'Worked'
                        ? '#E0EDE2'
                        : getDisplayStatus(currentStatus) === 'Expired'
                        ? '#FFF8E6'
                        : getDisplayStatus(currentStatus) === 'No Show'
                        ? '#FFEAEA'
                        : getDisplayStatus(currentStatus) === 'Unresolved'
                        ? '#FFE4CC'
                        : getDisplayStatus(currentStatus) === 'Applicants Applied'
                        ? '#E0E8ED'
                        : getDisplayStatus(currentStatus) === 'In Progress'
                        ? '#E0EDE2'
                        : '#F3F4F6',
                    color:
                      getDisplayStatus(currentStatus) === 'Confirmed'
                        ? '#3A7D44'
                        : getDisplayStatus(currentStatus) === 'Open'
                        ? '#3A5F7D'
                        : getDisplayStatus(currentStatus) === 'Cancelled'
                        ? '#C84648'
                        : getDisplayStatus(currentStatus) === 'Selected'
                        ? '#D4A123'
                        : getDisplayStatus(currentStatus) === 'Worked'
                        ? '#3A7D44'
                        : getDisplayStatus(currentStatus) === 'Expired'
                        ? '#D4A123'
                        : getDisplayStatus(currentStatus) === 'No Show'
                        ? '#C84648'
                        : getDisplayStatus(currentStatus) === 'Unresolved'
                        ? '#E67E22'
                        : getDisplayStatus(currentStatus) === 'Applicants Applied'
                        ? '#3A5F7D'
                        : getDisplayStatus(currentStatus) === 'In Progress'
                        ? '#3A7D44'
                        : '#6B7280',
                  }}
                >
                  {getDisplayStatus(currentStatus)}
                </span>
              )}
            </div>

            <p className="truncate text-sm text-gray-500">{person?.email}</p>

            <div className="flex items-center pt-1">
              <span
                className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                style={{ backgroundColor: '#EFF6FF', color: '#1E40AF' }}
              >
                Reliability:{' '}
                {typeof clinician?.reliability === 'number' && !isNaN(clinician.reliability)
                  ? `${clinician.reliability}%`
                  : 'N/A'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* ----- ACTIONS ----- */}
      <div className="flex w-full flex-col items-center gap-2 md:flex-row md:w-auto md:shrink-0 md:items-end">
        {/* Overlap warning */}
        {application.hasOverlap && application.overlappingShifts?.length ? (
          <div className="flex w-full items-center gap-1.5 rounded-lg bg-rose-50 p-2 text-xs font-medium text-rose-700 shadow-sm md:max-w-xs">
            <ExclamationTriangleIcon className="size-5 shrink-0 text-rose-600" />
            <span className="leading-tight">
              Overlaps with {application.overlappingShifts.length} shift(s)
            </span>
          </div>
        ) : null}

        {/* Error banner */}
        {errorMessage && (
          <div className="animate-fade-in flex w-full items-center justify-between gap-1.5 rounded-lg border border-rose-800 bg-rose-600 p-2 text-xs font-semibold text-white shadow-md md:max-w-xs">
            <div className="flex items-center gap-1.5">
              <ExclamationTriangleIcon className="size-5 shrink-0" />
              <span className="leading-tight">{errorMessage}</span>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setErrorMessage(null);
              }}
              className="-mr-1 rounded-full p-1 transition-colors duration-200 hover:bg-rose-700"
            >
              <XMarkIcon className="size-4" />
            </button>
          </div>
        )}

        {/* Buttons */}
        <div className="flex w-full items-center gap-3 md:w-auto">
          {currentStatus === 'PENDING' && (!hasSelectedApplication || isSelectedApplication) ? (
            <>
              <button
                onClick={handleAccept}
                disabled={isAccepting || isRejecting || disableApplicationActions}
                className="flex h-10 flex-1 items-center justify-center rounded-full bg-green-700 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-green-800 disabled:cursor-not-allowed disabled:bg-gray-300 md:w-28 md:flex-none"
                title={disableApplicationActions ? 'Shift start time has passed' : undefined}
              >
                {isAccepting ? <Spinner /> : 'Accept'}
              </button>

              <button
                onClick={handleReject}
                disabled={isAccepting || isRejecting || disableApplicationActions}
                className="flex h-10 flex-1 items-center justify-center rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300 md:w-28 md:flex-none"
                title={disableApplicationActions ? 'Shift start time has passed' : undefined}
              >
                {isRejecting ? <Spinner /> : 'Reject'}
              </button>
            </>
          ) : (
            <div className="flex w-full justify-end gap-3">
              {showRemove &&
                getDisplayStatus(currentStatus) !== 'Confirmed' &&
                getDisplayStatus(currentStatus) !== 'In Progress' &&
                getDisplayStatus(currentStatus) !== 'Worked' && (
                  <button
                    onClick={handleRemove}
                    disabled={isRemoveButtonDisabled}
                    className="flex h-10 items-center justify-center rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300 md:w-28"
                    title={
                      isRemoveDisabledDueToStart
                        ? 'Cannot remove selected clinician after shift has started'
                        : disableApplicationActions
                        ? 'Shift start time has passed'
                        : undefined
                    }
                  >
                    {isRemoving ? <Spinner /> : 'Remove'}
                  </button>
                )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApplicationRow;